import { isoDay } from '@/features/shared/format'
import { asRows } from '@/lib/api/list'
import { isoToDmy, tenureParts } from './lib/tenure'
import type { PersonelDetail, PersonelProfile, WorkScheduleDay } from './model'
import type { PersonelFormValues } from './schemas/personelFormSchema'
import type { BordroDokum, BordroDurum, BordroSatir, CalismaTuru, HedefSatir, IzinTalep, OdemeDurum, OdemeSatir, OdemeTur, Personel, PersonelDurum } from './types'

export function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

export function str(v: unknown, fallback = ''): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return fallback
}

export function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v.replace(',', '.')))) return Number(v.replace(',', '.'))
  return fallback
}

export function money(v: unknown): string {
  return num(v).toFixed(2)
}

export const rowsOf = (v: unknown): unknown[] => asRows(v)

const fullName = (row: Record<string, unknown>) => [str(row.first_name), str(row.last_name)].filter(Boolean).join(' ')

// --- BE ↔ FE sözlükleri (tek yerde; okuma ve yazma aynı tabloyu kullanır) ---

const WORK_STATUS: Record<PersonelDurum, string> = { aktif: 'ACTIVE', izinli: 'ON_LEAVE', deneme: 'PROBATION' }
const GENDER: Record<PersonelProfile['cinsiyet'], string> = { erkek: 'MALE', kadin: 'FEMALE', diger: 'OTHER' }
const MARITAL: Record<PersonelProfile['medeni'], string> = { bekar: 'SINGLE', evli: 'MARRIED', bosanmis: 'DIVORCED', dul: 'WIDOWED' }
const INSURANCE: Record<PersonelProfile['sigorta'], string> = { sgk: 'SGK', bagkur: 'BAGKUR', emekli: 'RETIRED_NONE' }
const EMPLOYMENT: Record<PersonelProfile['istihdam'], string> = { tam_zamanli: 'FULL_TIME', yari_zamanli: 'PART_TIME', stajyer: 'INTERN', sozlesmeli: 'CONTRACTOR' }
const WORK_MODEL: Record<CalismaTuru, string> = { tam_zamanli: 'OFFICE', hibrit: 'HYBRID', yari_zamanli: 'OFFICE' }
const EDUCATION: Record<PersonelProfile['egitim_durumu'], string> = { lise: 'HIGH_SCHOOL', onlisans: 'ASSOCIATE', lisans: 'BACHELOR', yuksek_lisans: 'MASTER', doktora: 'DOCTORATE' }

function reverse<K extends string>(map: Record<K, string>, value: unknown, fallback: K): K {
  const v = str(value).toUpperCase()
  return ((Object.keys(map) as K[]).find((k) => map[k] === v) ?? fallback) as K
}

/** "T.C." / "Türk" / "TR" → ISO-3166 alpha-2. */
export function nationalityCode(uyruk: string): string {
  const v = uyruk.trim().toLocaleLowerCase('tr').replace(/\./g, '')
  if (!v || v === 'tc' || v.startsWith('tür') || v.startsWith('turk') || v === 'tr') return 'TR'
  return /^[a-z]{2}$/i.test(uyruk.trim()) ? uyruk.trim().toUpperCase() : 'TR'
}

const nationalityLabel = (code: string) => (!code || code === 'TR' ? 'T.C.' : code)

export function personelDurum(row: Record<string, unknown>): PersonelDurum {
  return reverse(WORK_STATUS, row.work_status, 'aktif')
}

export function calismaTuru(row: Record<string, unknown>): CalismaTuru {
  if (str(row.work_model).toUpperCase() === 'HYBRID') return 'hibrit'
  if (str(row.employment_type).toUpperCase() === 'PART_TIME') return 'yari_zamanli'
  return 'tam_zamanli'
}

function kidemOf(hire: string) {
  if (!hire) return ''
  const { years, months } = tenureParts(hire)
  return years > 0 ? `${years} yıl ${months} ay` : `${months} ay`
}

export function mapPersonel(raw: unknown): Personel {
  const row = rec(raw)
  const department = rec(row.department)
  const position = rec(row.position)
  const manager = rec(row.manager)
  return {
    id: str(row.id),
    sicil: str(row.employee_number),
    ad: fullName(row),
    email: str(row.work_email ?? row.personal_email),
    telefon: str(row.phone),
    departman: str(department.name),
    pozisyon: str(position.title ?? row.job_title),
    ise_giris: isoDay(str(row.hire_date)),
    calisma_turu: calismaTuru(row),
    durum: personelDurum(row),
    yonetici: fullName(manager),
    lokasyon: str(rec(row.profile).branch),
    dogum: isoDay(str(row.birth_date)),
    egitim: str(rec(rec(row.profile).education).field_of_study),
    avatar: str(row.photo_url) || undefined,
    ortak: row.is_company_partner === true,
    emekli: str(row.insurance_type) === 'RETIRED_NONE',
  }
}

/** Ek bölümler (çalışma takvimi, ücret) yetkiye göre ayrı çekilir; yoksa varsayılan değer kalır. */
export function mapProfile(raw: unknown, personel: Personel, extra: { schedule?: unknown; compensations?: unknown } = {}): PersonelProfile {
  const row = rec(raw)
  const profile = rec(row.profile)
  const address = rec(profile.address)
  const emergency = rec(profile.emergency_contact)
  const education = rec(profile.education)
  const days = scheduleDays(extra.schedule)
  const firstWork = days.find((d) => d.is_working_day && d.start_time)
  const comp = latestCompensation(extra.compensations)
  return {
    ad: personel.ad,
    dogum: personel.dogum,
    tckn: str(row.identity_number),
    cinsiyet: reverse(GENDER, row.gender, 'diger'),
    medeni: reverse(MARITAL, row.marital_status, 'bekar'),
    uyruk: nationalityLabel(str(row.nationality_code)),
    foto_adi: '',
    departman_id: str(row.department_id),
    departman: personel.departman,
    pozisyon: personel.pozisyon,
    positionId: str(row.position_id),
    managerId: str(row.manager_employee_id),
    sube: str(profile.branch),
    istihdam: reverse(EMPLOYMENT, row.employment_type, 'tam_zamanli'),
    sigorta: reverse(INSURANCE, row.insurance_type, 'sgk'),
    ortak: row.is_company_partner === true,
    ortaklik_payi: str(row.partnership_share) ? String(Number(str(row.partnership_share))) : '',
    emeklilik_tarihi: isoDay(str(row.retired_at)),
    emeklilik_notu: str(row.retirement_note),
    calisma_turu: personel.calisma_turu,
    ise_giris: personel.ise_giris,
    calisma_baslangic: (firstWork?.start_time ?? '09:00').slice(0, 5),
    calisma_bitis: (firstWork?.end_time ?? '18:00').slice(0, 5),
    takvim: days,
    kidem: kidemOf(personel.ise_giris),
    yonetici: personel.yonetici,
    lokasyon: str(profile.branch),
    brut_maas: comp ? money(comp.gross_amount) : '',
    brut_kayitli: comp ? money(comp.gross_amount) : '',
    egitim_durumu: reverse(EDUCATION, education.level, 'lisans'),
    universite_id: str(education.institution_name),
    mezun_bolum: str(education.field_of_study),
    egitim: str(education.field_of_study),
    durum: personel.durum,
    sicil: personel.sicil,
    email: str(row.personal_email),
    telefon: str(row.phone),
    is_tel: str(row.work_phone),
    il_id: str(address.city_id),
    ilce_id: str(address.district_id),
    il_adi: str(address.city_name),
    ilce_adi: str(address.district_name),
    adres: str(address.line),
    acil_ad: str(emergency.full_name),
    acil_yakinlik: str(emergency.relationship),
    acil_telefon: str(emergency.phone),
    acil_email: str(emergency.email),
    acil_adres: str(emergency.address),
    panel_erisim: Boolean(row.user_id),
    firma_email: str(row.work_email),
    belgeler: [],
  }
}

export function scheduleDays(raw: unknown): WorkScheduleDay[] {
  return rowsOf(rec(raw).days ?? raw)
    .map((d) => {
      const day = rec(d)
      return {
        weekday: num(day.weekday),
        is_working_day: day.is_working_day === true,
        start_time: str(day.start_time).slice(0, 5) || undefined,
        end_time: str(day.end_time).slice(0, 5) || undefined,
        end_day_offset: num(day.end_day_offset),
        break_minutes: num(day.break_minutes),
        timezone: str(day.timezone, 'Europe/Istanbul'),
      }
    })
    .filter((d) => d.weekday >= 1 && d.weekday <= 7)
    .sort((a, b) => a.weekday - b.weekday)
}

/**
 * Formdaki mesai saatlerini haftalık takvime uygular. Kayıtlı takvim varsa yalnızca çalışılan günlerin saatleri
 * değişir (hafta sonu / özel gün düzeni korunur); yoksa Pzt–Cum çalışma günü varsayılır.
 */
export function scheduleBody(current: WorkScheduleDay[], start: string, end: string) {
  const base: WorkScheduleDay[] = current.length === 7
    ? current
    : Array.from({ length: 7 }, (_, i) => ({ weekday: i + 1, is_working_day: i < 5, end_day_offset: 0, break_minutes: 60, timezone: 'Europe/Istanbul' }))
  return {
    days: base.map((d) =>
      d.is_working_day
        ? { ...d, start_time: start, end_time: end, end_day_offset: end <= start ? 1 : 0 }
        : { weekday: d.weekday, is_working_day: false, end_day_offset: 0, break_minutes: 0, timezone: d.timezone },
    ),
  }
}

function latestCompensation(raw: unknown) {
  const items = rowsOf(raw).map(rec)
  return items.find((c) => !str(c.valid_to)) ?? items[0]
}

// --- Bordro / ödeme / izin / performans ---

const BORDRO_STATUS: Record<BordroDurum, string> = { taslak: 'DRAFT', kontrol: 'IN_REVIEW', onay: 'FINAL' }
export const bordroApiStatus = (durum: BordroDurum) => BORDRO_STATUS[durum]

function bordroDurum(value: unknown): BordroDurum {
  const status = str(value).toUpperCase()
  if (status === 'FINAL') return 'onay'
  if (status === 'IN_REVIEW' || status === 'BLOCKED_PENDING_OT') return 'kontrol'
  return 'taslak'
}

export function mapBordroRow(raw: unknown, people: Personel[], period: string): BordroSatir {
  const row = rec(raw)
  const personelId = str(row.employee_id)
  const person = people.find((item) => item.id === personelId)
  const dokum = mapDokum(row.statutory)
  const gecmisAktarim = str(row.source).toUpperCase() === 'HISTORICAL'
  return {
    id: str(row.id, `${personelId}-${period}`),
    personel_id: personelId,
    ad: person?.ad ?? '',
    departman: person?.departman ?? '',
    unvan: person?.pozisyon ?? '',
    sicil: person?.sicil ?? '',
    brut: money(row.gross_salary),
    ek: money(num(row.bonus_total) + num(row.allowance_total)),
    kesinti: dokum || gecmisAktarim ? money(num(row.gross_salary) + num(row.bonus_total) - num(row.net_salary)) : money(row.sgk_personal_deduction),
    net: money(row.total_payable_salary ?? row.net_salary),
    durum: bordroDurum(row.status),
    donem: str(row.period, period),
    blokeNedeni: str(row.blocked_reason) || undefined,
    dokum,
    gecmisAktarim,
  }
}

function mapDokum(raw: unknown): BordroDokum | undefined {
  const d = rec(raw)
  if (!str(d.params_from)) return undefined
  return {
    sgkMatrah: money(d.sgk_base),
    sgkIsci: money(d.employee_sgk),
    issizlikIsci: money(d.employee_unemployment),
    gvMatrah: money(d.income_tax_base),
    kumulatifMatrah: money(d.cumulative_tax_base),
    gelirVergisi: money(d.income_tax),
    gvIstisna: money(d.income_tax_exemption),
    damgaVergisi: money(d.stamp_tax),
    dvIstisna: money(d.stamp_tax_exemption),
    sgkIsveren: money(d.employer_sgk),
    issizlikIsveren: money(d.employer_unemployment),
    tesvikOrani: str(d.employer_discount_rate),
    tesvik: money(d.employer_discount),
    isverenMaliyet: money(d.employer_cost),
    sgkToplam: money(d.sgk_total),
    muhtasar: money(d.withholding_total),
  }
}

export function payrollRows(raw: unknown, people: Personel[], period: string): BordroSatir[] {
  return rowsOf(rec(raw).payrolls ?? raw).map((item) => mapBordroRow(item, people, period))
}

function odemeTur(value: unknown): OdemeTur {
  const kind = str(value).toUpperCase()
  if (kind === 'ADVANCE') return 'avans'
  if (kind === 'BONUS') return 'prim'
  if (kind === 'MEAL') return 'yemek'
  if (kind === 'TRANSPORT') return 'ulasim'
  if (kind === 'EXPENSE') return 'masraf'
  return 'maas'
}

function odemeDurum(row: Record<string, unknown>): OdemeDurum {
  if (str(row.status).toUpperCase() === 'PAID') return 'odendi'
  if (str(row.approval_status).toUpperCase() === 'PENDING_OWNER') return 'onay_bekliyor'
  return 'planlandi'
}

export function donemLabel(period: string) {
  if (!/^\d{4}-\d{2}$/.test(period)) return period
  const label = new Date(`${period}-01T00:00:00`).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
  return label.charAt(0).toLocaleUpperCase('tr') + label.slice(1)
}

export type OdemeView = OdemeSatir & { aciklama: string; yontem: string; iban: string; banka: string; onaylandi: boolean; reddedildi: boolean; periyot: string; gecmisAktarim: boolean }

export function mapPayment(raw: unknown, people: Personel[]): OdemeView {
  const row = rec(raw)
  const personelId = str(row.employee_id)
  const person = people.find((item) => item.id === personelId)
  const period = str(row.period)
  const approval = str(row.approval_status).toUpperCase()
  return {
    id: str(row.id),
    personel_id: personelId,
    ad: person?.ad ?? '',
    unvan: person?.pozisyon ?? '',
    birim: person?.departman ?? '',
    tur: odemeTur(row.payment_type),
    donem: donemLabel(period),
    tutar: money(row.amount),
    tarih: isoDay(str(row.payment_date ?? row.created_at)) || '—',
    durum: odemeDurum(row),
    aciklama: str(row.description) || donemLabel(period),
    yontem: 'Banka',
    iban: str(row.masked_iban),
    banka: str(row.bank_name),
    onaylandi: approval === 'APPROVED',
    reddedildi: approval === 'REJECTED' || approval === 'CANCELLED',
    /** YYYY-MM (filtre/banka dosyası için); `donem` görünen etikettir. */
    periyot: period,
    gecmisAktarim: row.is_historical === true,
  }
}

function leaveDurum(value: unknown): IzinTalep['durum'] {
  const status = str(value).toUpperCase()
  if (status === 'APPROVED') return 'onaylandi'
  if (status === 'REJECTED' || status === 'CANCELLED') return 'red'
  return 'bekliyor'
}

export function mapLeave(raw: unknown): IzinTalep {
  const row = rec(raw)
  const start = isoDay(str(row.start_date))
  const end = isoDay(str(row.end_date))
  const days = num(row.total_days)
  return {
    id: str(row.id),
    tur: str(row.leave_type_name, 'İzin'),
    baslangic: start,
    bitis: end || start,
    gun: days ? `${String(days).replace('.', ',')} gün` : '—',
    durum: leaveDurum(row.status),
  }
}

function goalDurum(value: unknown): HedefSatir['durum'] {
  const status = str(value)
  if (status === 'done') return 'tamam'
  if (status === 'at_risk') return 'risk'
  return 'yolda'
}

function scoreLabel(value: number) {
  return `${value.toFixed(1).replace('.', ',')} / 5`
}

export function mapPerformance(raw: unknown, personel: Personel): PersonelDetail['perf'] {
  const row = rec(raw)
  const goals = rowsOf(row.goals).map((item) => {
    const goal = rec(item)
    return {
      id: str(goal.id),
      ad: str(goal.name),
      aciklama: str(goal.description),
      ilerleme: num(goal.progress),
      durum: goalDurum(goal.status),
      son_tarih: isoDay(str(goal.due_date)),
    }
  })
  const trainings = rowsOf(row.trainings).map((item) => {
    const training = rec(item)
    const done = str(training.status) === 'done'
    const date = isoDay(str(training.date))
    return {
      id: str(training.id),
      t: date ? isoToDmy(date) : '—',
      b: str(training.title),
      a: str(training.provider) || str(training.certificate),
      badge: (done ? 'Sertifika' : 'Planlandı') as 'Sertifika' | 'Planlandı',
      state: (done ? 'done' : 'planned') as 'done' | 'planned',
    }
  })
  const reviews = rowsOf(row.reviews).map(rec)
  const score = num(row.avg_score)
  const counts = rec(row.training_counts)
  return {
    kpi: {
      skor: score ? scoreLabel(score) : '—',
      skorDeger: score,
      skor_tarih: isoDay(str(reviews[0]?.created_at)),
      hedefler: `%${Math.round(num(row.goal_completion_pct))}`,
      hedef_yil: String(new Date().getFullYear()),
      egitim_tamam: String(num(counts.done)),
      egitim_plan: String(num(counts.planned)),
      sonraki: '',
    },
    hedefler: goals,
    notlar: reviews.map((review, index) => {
      const date = isoDay(str(review.created_at))
      return {
        id: str(review.id, `${personel.id}-r${index}`),
        metin: str(review.body),
        yazar: str(review.author_name) || '—',
        unvan: str(review.author_title),
        tarih: date ? isoToDmy(date) : '—',
      }
    }),
    yetkinlikler: rowsOf(row.skills).map((item) => {
      const skill = rec(item)
      const value = num(skill.score)
      return { id: str(skill.id), ad: str(skill.name), skor: value.toFixed(1).replace('.', ','), pct: Math.round(value * 20) }
    }),
    egitim: trainings,
  }
}

const EMPTY_PERF: PersonelDetail['perf'] = {
  kpi: { skor: '—', skorDeger: 0, skor_tarih: '', hedefler: '%0', hedef_yil: String(new Date().getFullYear()), egitim_tamam: '0', egitim_plan: '0', sonraki: '' },
  hedefler: [],
  notlar: [],
  yetkinlikler: [],
  egitim: [],
}

function checklist(profile: PersonelProfile, missingDocs?: number) {
  const groups: Record<string, string[]> = {
    kisisel: [profile.tckn, profile.dogum, profile.uyruk],
    iletisim: [profile.email || profile.firma_email, profile.telefon, profile.adres, profile.il_id],
    organizasyon: [profile.departman, profile.pozisyon, profile.sube],
    acil: [profile.acil_ad, profile.acil_telefon, profile.acil_yakinlik],
  }
  const values = Object.values(groups).flat()
  const filled = values.filter((value) => value && value.trim()).length
  const items = Object.entries(groups).map(([key, list]) => ({ key, done: list.every((value) => value && value.trim()) }))
  if (missingDocs !== undefined) items.push({ key: 'belge', done: missingDocs === 0 })
  const total = values.length + (missingDocs !== undefined ? 1 : 0)
  const score = filled + (missingDocs === 0 ? 1 : 0)
  return { pct: total ? Math.round((score / total) * 100) : 0, checklist: items }
}

/** Sekme verileri ayrı ayrı (ve yetkiye göre) yüklenir; yüklenmemiş bölüm boş varsayılanla döner. */
export type DetailSources = {
  history?: unknown
  leaves?: unknown
  compensations?: unknown
  payrolls?: unknown
  payments?: unknown
  allowances?: unknown
  /** Özlük dosyası: `{documents, missing}` (yüklenen taramalar + eksik zorunlu türler). */
  personnelFile?: unknown
  /** Belge modülü: şablondan üretilen belge talepleri ve eksikleri. */
  cases?: unknown
  missing?: unknown
  performance?: unknown
}

export function buildDetail(personel: Personel, profile: PersonelProfile, src: DetailSources): PersonelDetail {
  const leaveData = rec(src.leaves)
  const leaves = rowsOf(src.leaves).map(mapLeave)
  const balance = rec(leaveData.balance)
  const approved = leaves.filter((item) => item.durum === 'onaylandi')
  const gross = num(profile.brut_kayitli)
  const payrolls = payrollRows(src.payrolls, [personel], '')
  const allowanceTotal = rowsOf(src.allowances)
    .map(rec)
    .filter((a) => str(a.status).toUpperCase() === 'APPROVED' && !str(a.valid_to))
    .reduce((sum, a) => sum + num(a.amount), 0)
  const payments = rowsOf(src.payments).map((item) => mapPayment(item, [personel]))
  const year = String(new Date().getFullYear())
  const paid = payments.filter((item) => item.durum === 'odendi' && item.tarih.startsWith(year))
  const waiting = payments.filter((item) => item.durum !== 'odendi' && !item.reddedildi)
  const advances = waiting.filter((item) => item.tur === 'avans')
  const file = rec(src.personnelFile)
  const soon = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10)
  const today = new Date().toISOString().slice(0, 10)
  const uploaded = rowsOf(file.documents).map((item) => {
    const row = rec(item)
    const validUntil = str(row.valid_until)
    return {
      kaynak: 'ozluk' as const,
      kindKey: str(row.kind_key),
      dosyaAdi: str(row.original_name) || str(row.title),
      id: str(row.id),
      ad: str(row.title, 'Belge'),
      kategori: str(row.original_name) || str(row.title),
      klasor: 'Özlük',
      kind: 'file' as const,
      tur: str(row.content_type).split('/').pop()?.toUpperCase() ?? '—',
      boyut: fileSizeLabel(num(row.size_bytes)),
      // Belge tarihi (geçmiş tarihli yüklemelerde düzenlenme tarihi) yoksa yükleme tarihi gösterilir.
      tarih: str(row.document_date) || isoDay(str(row.created_at)) || '—',
      gecerlilik: validUntil || 'Süresiz',
      durum: (validUntil && validUntil < today ? 'eksik' : validUntil && validUntil <= soon ? 'suresi_doluyor' : 'tamam') as 'tamam' | 'eksik' | 'suresi_doluyor',
    }
  })
  const missingKinds = rowsOf(file.missing).map((item) => {
    const row = rec(item)
    return { kaynak: 'tur' as const, kindKey: str(row.key), id: `kind-${str(row.key)}`, ad: str(row.title, 'Belge'), kategori: 'Özlük', klasor: 'Özlük', kind: 'file' as const, tur: '—', boyut: '—', tarih: '—', gecerlilik: '—', durum: 'eksik' as const }
  })
  const missingRows = rowsOf(src.missing).map((item, index) => {
    const row = rec(item)
    return {
      kaynak: 'gereksinim' as const,
      templateId: str(row.template_id),
      id: str(row.requirement_id ?? row.id, `missing-${index}`),
      ad: str(row.name ?? row.title, 'Belge'),
      kategori: str(row.type_name ?? row.category, 'Özlük'),
      klasor: str(row.type_name ?? row.category, 'Özlük'),
      kind: 'file' as const,
      tur: '—',
      boyut: '—',
      tarih: '—',
      gecerlilik: '—',
      durum: 'eksik' as const,
    }
  })
  const files = rowsOf(src.cases).map((item, index) => {
    const row = rec(item)
    const status = str(row.status).toLowerCase()
    const due = isoDay(str(row.due_at))
    return {
      kaynak: 'case' as const,
      caseStatus: status,
      sonTarih: due,
      id: str(row.id, `doc-${index}`),
      ad: str(row.title ?? row.name ?? row.template_name, 'Belge'),
      kategori: str(row.type_name ?? row.category, 'Özlük'),
      klasor: str(row.type_name ?? row.category, 'Özlük'),
      kind: 'file' as const,
      tur: str(row.mime_type, 'PDF'),
      boyut: str(row.size),
      tarih: isoDay(str(row.submitted_at ?? row.created_at)) || '—',
      gecerlilik: due ? `Son: ${due.split('-').reverse().join('.')}` : isoDay(str(row.expires_at)) || 'Süresiz',
      // Sunucu durumları küçük harflidir; incelemedeki belge eksik sayılmaz.
      durum: (['completed', 'waived'].includes(status) ? 'tamam' : status === 'awaiting_review' ? 'inceleniyor' : 'eksik') as 'tamam' | 'eksik' | 'inceleniyor',
    }
  })
  const liste = [...uploaded, ...files, ...missingKinds, ...missingRows]
  const tamam = liste.filter((item) => item.durum !== 'eksik').length
  const eksik = liste.filter((item) => item.durum === 'eksik')
  const yaklasan = uploaded
    .filter((item) => item.durum === 'suresi_doluyor')
    .map((item) => ({ id: item.id, ad: item.ad, tarih: item.gecerlilik, kalan: `${Math.max(0, Math.round((new Date(item.gecerlilik).getTime() - Date.now()) / 86_400_000))} gün` }))
  const lastPayroll = payrolls[0]
  const net = lastPayroll ? num(lastPayroll.net) : gross
  const kesinti = lastPayroll ? num(lastPayroll.kesinti) : 0
  const history = rowsOf(src.history).map(rec)
  return {
    gecmis: history.map((h, i) => ({
      t: isoToDmy(str(h.valid_from)),
      b: [str(h.department_name), str(h.position_title)].filter(Boolean).join(' · ') || '—',
      a: str(h.change_reason) || (str(h.manager_name) ? `Yönetici: ${str(h.manager_name)}` : ''),
      old: i > 0,
    })),
    profil: checklist(profile, src.personnelFile === undefined ? undefined : missingKinds.length),
    ucret: {
      brut: money(gross),
      net: money(net),
      yan: money(allowanceTotal),
      kesintiToplam: money(kesinti),
      // İşveren maliyeti: son bordrodaki brüt + işveren SGK/işsizlik payı − teşvik (+ yan haklar).
      iseveren: money((lastPayroll?.dokum ? num(lastPayroll.dokum.isverenMaliyet) : gross) + allowanceTotal),
      kazanc: [{ label: 'Brüt Ücret', amount: money(gross) }, ...(allowanceTotal ? [{ label: 'Yan hak', amount: money(allowanceTotal) }] : [])],
      kesinti: lastPayroll?.dokum ? bordroKesintiler(lastPayroll.dokum) : kesinti ? [{ label: 'SGK işçi payı', amount: money(kesinti) }] : [],
      gecmis: payrolls.map((row) => ({
        id: row.id,
        period: row.donem ?? '',
        donem: donemLabel(row.donem ?? ''),
        brut: row.brut,
        net: row.net,
        durum: row.durum,
        gecmisAktarim: !!row.gecmisAktarim,
      })),
    },
    izin: {
      kpi: {
        yillik_hak: String(num(balance.entitlement) + num(balance.transferred)),
        kullanilan: String(num(balance.used)),
        kalan: String(num(balance.remaining)),
        mesai: '0',
      },
      talepler: leaves,
      puantaj: [],
      izinGunleri: approved.flatMap((item) => [item.baslangic, item.bitis].filter(Boolean)),
      onay: [],
    },
    belge: {
      liste,
      ozet: {
        pct: liste.length ? Math.round((tamam / liste.length) * 100) : 0,
        tamam,
        eksik: eksik.length,
        suresi_gecen: uploaded.filter((item) => item.gecerlilik !== 'Süresiz' && item.gecerlilik < today).length,
      },
      eksik: eksik.map((item) => ({ id: item.id, ad: item.ad })),
      yaklasan,
      sozlesme: {
        tip: profile.istihdam === 'sozlesmeli' ? 'Belirli Süreli' : 'Belirsiz Süreli',
        baslangic: personel.ise_giris ? isoToDmy(personel.ise_giris) : '—',
        bitis: 'Belirsiz',
        deneme: personel.durum === 'deneme' ? 'Devam ediyor' : 'Tamamlandı',
        istihdam: profile.istihdam,
      },
    },
    odeme: {
      yilToplam: money(paid.reduce((sum, item) => sum + num(item.tutar), 0)),
      yilAdet: paid.length,
      bekleyen: money(waiting.reduce((sum, item) => sum + num(item.tutar), 0)),
      bekleyenAdet: waiting.length,
      avans: money(advances.reduce((sum, item) => sum + num(item.tutar), 0)),
      avansAdet: advances.length,
      liste: payments,
      timeline: [],
      banka: { ad: payments.find((item) => item.banka)?.banka || '—', iban: payments.find((item) => item.iban)?.iban || '—' },
    },
    perf: src.performance === undefined ? EMPTY_PERF : mapPerformance(src.performance, personel),
  }
}

function fileSizeLabel(bytes: number) {
  if (!bytes) return '—'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

export function splitName(ad: string) {
  const parts = ad.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return { first_name: parts[0] ?? '', last_name: parts[0] ?? '' }
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts.at(-1) ?? '' }
}

const orNull = (v: string | undefined) => (v && v.trim() ? v.trim() : undefined)

/** Form → BE personel gövdesi (POST ve PUT ortak; profil alt kayıtları aynı istekte gider). */
export function employeeBody(values: PersonelFormValues) {
  const name = splitName(values.ad)
  const tckn = values.tckn.trim()
  return {
    ...name,
    birth_date: values.dogum,
    hire_date: values.ise_giris,
    identity_number: tckn && !tckn.includes('*') ? tckn : undefined,
    nationality_code: nationalityCode(values.uyruk),
    gender: GENDER[values.cinsiyet as PersonelProfile['cinsiyet']],
    marital_status: MARITAL[values.medeni as PersonelProfile['medeni']],
    department_id: values.departman_id,
    position_id: orNull(values.pozisyon),
    manager_employee_id: orNull(values.yonetici),
    employment_type: EMPLOYMENT[values.istihdam as PersonelProfile['istihdam']],
    work_model: WORK_MODEL[values.calisma_turu as CalismaTuru],
    work_status: WORK_STATUS[values.durum as PersonelDurum],
    insurance_type: INSURANCE[values.sigorta as PersonelProfile['sigorta']],
    is_company_partner: values.ortak,
    partnership_share: values.ortak ? values.ortaklik_payi.trim() : '',
    retired_at: values.emeklilik_tarihi,
    retirement_note: values.emeklilik_notu.trim(),
    phone: values.telefon,
    work_phone: orNull(values.is_tel),
    personal_email: orNull(values.email),
    work_email: orNull(values.firma_email),
    profile: {
      branch: orNull(values.sube),
      address: values.il_id && values.ilce_id && values.adres.trim() ? { city_id: values.il_id, district_id: values.ilce_id, line: values.adres.trim() } : undefined,
      emergency_contact: values.acil_ad.trim()
        ? {
            full_name: values.acil_ad.trim(),
            relationship: values.acil_yakinlik.trim(),
            phone: values.acil_telefon,
            email: orNull(values.acil_email),
            address: orNull(values.acil_adres),
          }
        : undefined,
      education: {
        level: EDUCATION[values.egitim_durumu as PersonelProfile['egitim_durumu']],
        institution_name: orNull(values.universite_id),
        field_of_study: orNull(values.mezun_bolum),
      },
    },
  }
}

export const workStatusOf = (durum: PersonelDurum) => WORK_STATUS[durum]

export function mailboxPayload(values: {
  mailbox_assign?: boolean
  mailbox_provider_id?: string
  mailbox_secret?: string
  mailbox_signature?: string
}) {
  if (!values.mailbox_assign) return undefined
  const providerId = String(values.mailbox_provider_id ?? '').trim()
  const secret = String(values.mailbox_secret ?? '').trim()
  if (!providerId || !secret) return undefined
  const signature = String(values.mailbox_signature ?? '').trim()
  return {
    provider_id: providerId,
    secret,
    ...(signature ? { signature_html: signature } : {}),
  }
}

/** Bordro kesinti kalemleri (sunucu dökümünden). */
export function bordroKesintiler(d: BordroDokum) {
  return [
    { label: 'SGK işçi payı (%14)', amount: d.sgkIsci },
    { label: 'İşsizlik sigortası işçi payı (%1)', amount: d.issizlikIsci },
    { label: 'Gelir vergisi', amount: d.gelirVergisi },
    { label: 'Damga vergisi', amount: d.damgaVergisi },
  ]
}
